import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./voice-orb.css";
import { fetchFillerAudio, fetchFillerCount, sendChatMessage, synthesizeSpeech, SableApiError, } from "../api/sableApi";
import { ParticlesOrb } from "./orb/particles-orb";
import { useMatchDocumentBackground } from "./useMatchDocumentBackground";
import { useMicLevel } from "./useMicLevel";
import { usePollyAudio } from "./usePollyAudio";
import { useSpeakingPulse } from "./useSpeakingPulse";
import { useSpeechRecognition } from "./useSpeechRecognition";
// The orb itself is "Particles Orb" from https://voiceorbs.vercel.app/orbs/particles-orb (ported
// into ./orb/ -- see orb/orb-state.ts's header comment for how/why) -- a real, dependency-free
// Canvas 2D 720-particle rotating sphere. `colorFrom`/`colorTo` are swapped per state below
// (idle/listening/thinking/speaking) so it's visually clear what's happening at any moment (§4.2).
const STATE_GRADIENT = {
    idle: ["#2f6f7d", "#5fb3c7"],
    listening: ["#e2a53b", "#f6cf7a"],
    thinking: ["#7c5cff", "#b79cff"],
};
function hexToRgbTuple(hex) {
    const m = hex.replace("#", "");
    const full = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
    const num = parseInt(full, 16);
    if (Number.isNaN(num))
        return [196, 30, 30];
    return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}
function lightenHex(hex, amount = 0.4) {
    const [r, g, b] = hexToRgbTuple(hex);
    const mix = (c) => Math.round(c + (255 - c) * amount);
    return `#${[mix(r), mix(g), mix(b)].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}
function MicIcon() {
    return (_jsxs("svg", { width: "22", height: "22", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" }), _jsx("path", { d: "M19 10v2a7 7 0 0 1-14 0v-2" }), _jsx("line", { x1: "12", y1: "19", x2: "12", y2: "23" }), _jsx("line", { x1: "8", y1: "23", x2: "16", y2: "23" })] }));
}
function ChatIcon() {
    return (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("path", { d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" }) }));
}
function StopIcon() {
    return (_jsx("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "currentColor", children: _jsx("rect", { x: "5", y: "5", width: "14", height: "14", rx: "2" }) }));
}
const FILLER_GRACE_MS = 900;
const FILLER_GAP_MS = 150;
function visitorKeyStorageKey(token) {
    return `sable-visitor-key-${token}`;
}
function getOrCreateVisitorKey(token) {
    try {
        const existing = window.localStorage.getItem(visitorKeyStorageKey(token));
        if (existing)
            return existing;
        const fresh = crypto.randomUUID();
        window.localStorage.setItem(visitorKeyStorageKey(token), fresh);
        return fresh;
    }
    catch {
        return crypto.randomUUID();
    }
}
export default function VoiceOrbExperience({ token, directory }) {
    const visitorKey = useRef(getOrCreateVisitorKey(token));
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [toast, setToast] = useState(null);
    const [speakAloud, setSpeakAloud] = useState(true);
    const [helpOpen, setHelpOpen] = useState(false);
    const [mode, setMode] = useState("voice");
    // Continuous "conversation mode" (user's own request): one tap turns it on and starts listening;
    // once a question is heard, it's answered and listening resumes automatically with no further
    // taps, for as long as this stays on. A second tap turns it off. Refs mirror the relevant React
    // state for use inside async callbacks (`speakReply`, the "ended without a result" effect below)
    // without stale-closure reads.
    const [conversationActive, setConversationActive] = useState(false);
    const conversationActiveRef = useRef(false);
    const turnHandledRef = useRef(false);
    // True for the whole of `speakReply` -- covers the real gap between a filler clip ending and the
    // real reply's own audio actually starting (the synthesizeSpeech network round-trip), during which
    // neither `speaking` nor `sending` is true. See `speakReply`'s own comment for why this exists.
    const [preparingToSpeak, setPreparingToSpeak] = useState(false);
    const [taglineVisible, setTaglineVisible] = useState(false);
    const taglineTimerRef = useRef(null);
    const mic = useMicLevel();
    const pollyAudio = usePollyAudio();
    const ttsFallback = useSpeakingPulse();
    const messagesEndRef = useRef(null);
    const orbWrapRef = useRef(null);
    const orbLevelRef = useRef(0);
    const [orbSize, setOrbSize] = useState(168);
    const fillerCountRef = useRef(0);
    const usedFillerIndicesRef = useRef([]);
    const activeFillerPlayRef = useRef(null);
    useLayoutEffect(() => {
        const el = orbWrapRef.current;
        if (!el)
            return;
        const measure = () => setOrbSize(Math.round(el.clientWidth));
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    useEffect(() => {
        fetchFillerCount()
            .then((count) => {
            fillerCountRef.current = count;
        })
            .catch(() => {
            fillerCountRef.current = 0;
        });
    }, []);
    useEffect(() => {
        conversationActiveRef.current = conversationActive;
    }, [conversationActive]);
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [messages, sending]);
    // Voice orb is always dark regardless of system light/dark theme (unlike the chat/storyteller
    // canvas, which follows the system) -- see useMatchDocumentBackground's own doc comment for the
    // underlying iOS Safari overscroll bug this fixes.
    useMatchDocumentBackground("#0b0d12");
    useEffect(() => {
        if (!toast)
            return;
        const t = setTimeout(() => setToast(null), 3200);
        return () => clearTimeout(t);
    }, [toast]);
    const speaking = pollyAudio.speaking || ttsFallback.speaking;
    const speakReply = async (reply) => {
        // Covers the real gap between the filler ending and the real reply's own audio actually
        // starting (the synthesizeSpeech network round-trip) -- confirmed live as a visible issue: with
        // nothing bridging it, `orbState` fell through to "idle" for that whole window (neither
        // `speaking` nor `sending` -- `sending` already flipped false once the chat TEXT reply arrived,
        // well before its audio exists -- were true), reading as the orb's animation freezing/resetting
        // right as the filler handed off to the real answer.
        setPreparingToSpeak(true);
        try {
            // Let whatever filler clip is currently mid-sentence actually finish before the real answer
            // starts, per the user's own request, rather than cutting it off abruptly the instant the
            // real reply is ready -- a clean handoff instead of an interruption.
            if (activeFillerPlayRef.current) {
                await activeFillerPlayRef.current;
            }
            try {
                const audioBlob = await synthesizeSpeech(token, reply, visitorKey.current);
                await pollyAudio.play(audioBlob);
            }
            catch {
                // Polly call failed (rate-limited, AWS hiccup) -- fall back to the browser's own voice
                // rather than the orb going silent.
                ttsFallback.speak(reply);
            }
        }
        finally {
            setPreparingToSpeak(false);
        }
        // Continuous "conversation mode": once the real reply is done (however it was spoken), resume
        // listening automatically so the next question doesn't need another tap. Deliberately OUTSIDE
        // the try/catch above -- confirmed live as a real bug otherwise: a real `SpeechRecognition`
        // instance's `.start()` can throw synchronously (a native `InvalidStateError` if called too
        // soon after a previous session, which `useSpeechRecognition.ts`'s own abort-based cleanup
        // doesn't fully eliminate the timing window for) -- with `startListening()` inside that catch
        // block, such a throw was being silently swallowed as if it were a Polly playback failure,
        // triggering the browser-TTS fallback instead of surfacing the real problem, and permanently
        // breaking the conversation loop with no visible error at all ("stops responding after the
        // first question"). It's now its own statement, after the try/finally has fully settled either
        // way, so an error here can't be misattributed to -- or silence -- anything above it.
        //
        // A short delay before restarting, rather than calling `startListening()` immediately: Polly
        // playback goes back through a plain `<audio>` element (see `usePollyAudio.ts`'s own header
        // comment) for better sound quality than the Web Audio alternative. `usePollyAudio.ts`'s own
        // `releaseAudio` now actively tears the element's media resource down the instant it finishes
        // (confirmed live as the real fix -- this is a WebKit-engine issue, not iOS-only: reproduced on
        // Mac Safari too, as "fails to reset after the initial question"), so this delay is now just a
        // small safety margin on top of that active teardown, not the primary fix it was before.
        if (conversationActiveRef.current) {
            setTimeout(() => {
                if (conversationActiveRef.current)
                    startListening();
            }, 250);
        }
    };
    const sendMessage = async (text) => {
        const trimmed = text.trim();
        if (!trimmed || sending)
            return;
        setInput("");
        setMessages((prev) => [...prev, { from: "visitor", text: trimmed }]);
        setSending(true);
        try {
            const reply = await sendChatMessage(token, trimmed, visitorKey.current);
            setMessages((prev) => [...prev, { from: "assistant", text: reply }]);
            setSending(false);
            if (speakAloud)
                speakReply(reply);
        }
        catch (err) {
            setSending(false);
            if (err instanceof SableApiError && err.status === 429) {
                setToast(err.message);
            }
            else {
                setToast("Couldn't send that. Try again in a moment.");
            }
        }
    };
    const recognition = useSpeechRecognition((transcript) => {
        // Marks this listen session as "produced a real result" -- the "ended without a result" effect
        // below uses this to tell a genuine answered turn (where `speakReply` above already owns
        // restarting listening once the reply finishes) apart from a silence/no-speech timeout (where
        // restarting immediately, right here, is correct).
        turnHandledRef.current = true;
        sendMessage(transcript);
    });
    const startListening = () => {
        turnHandledRef.current = false;
        if (recognition.supported)
            recognition.start();
        else
            mic.start();
    };
    // Continuous "conversation mode": if listening ends with no result and no real error (a plain
    // silence/no-speech timeout -- the single most common way a listen session ends when the visitor
    // just hasn't said anything yet), and nothing else is already in flight, resume listening rather
    // than requiring another tap. A genuine error (e.g. permission denied) does NOT auto-retry --
    // repeatedly restarting against a problem that won't resolve itself would just loop forever -- and
    // turns conversation mode off entirely so the UI doesn't keep claiming to be listening.
    useEffect(() => {
        if (recognition.listening)
            return;
        if (!conversationActiveRef.current)
            return;
        if (turnHandledRef.current)
            return;
        if (sending || speaking)
            return;
        if (recognition.error) {
            setConversationActive(false);
            return;
        }
        const t = setTimeout(() => {
            if (conversationActiveRef.current)
                startListening();
        }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recognition.listening]);
    // The "thinking" filler loop -- starts after a short grace period (a fast reply shouldn't play
    // any filler at all), then plays random non-repeating clips back to back until the real reply
    // arrives (`sending` flips false). Deliberately does NOT force-stop a clip that's still playing
    // when that happens -- `cancelled` just stops the loop from fetching/starting another one;
    // `speakReply` above waits for `activeFillerPlayRef` (whatever's already in flight) to finish
    // naturally before speaking the real answer, so a filler is never cut off mid-sentence.
    useEffect(() => {
        if (!sending)
            return;
        let cancelled = false;
        const pickIndex = () => {
            const count = fillerCountRef.current;
            if (count <= 0)
                return null;
            if (usedFillerIndicesRef.current.length >= count)
                usedFillerIndicesRef.current = [];
            let idx;
            do {
                idx = Math.floor(Math.random() * count);
            } while (usedFillerIndicesRef.current.includes(idx));
            usedFillerIndicesRef.current.push(idx);
            return idx;
        };
        const run = async () => {
            await new Promise((resolve) => setTimeout(resolve, FILLER_GRACE_MS));
            while (!cancelled) {
                const idx = pickIndex();
                if (idx === null)
                    return;
                try {
                    const blob = await fetchFillerAudio(idx);
                    if (cancelled)
                        return;
                    const playPromise = pollyAudio.play(blob);
                    activeFillerPlayRef.current = playPromise;
                    await playPromise;
                    activeFillerPlayRef.current = null;
                }
                catch {
                    // Couldn't fetch/play this one -- just try the next, the loop below still exits
                    // correctly once the real reply arrives.
                }
                if (cancelled)
                    return;
                await new Promise((resolve) => setTimeout(resolve, FILLER_GAP_MS));
            }
        };
        run();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sending]);
    // Mic/recognition and TTS are mutually exclusive states for the orb's one "who's talking" signal
    // -- stop listening to your own mic/voice the moment the Assistant starts talking (whether that's
    // a filler clip or the real reply).
    useEffect(() => {
        if (speaking) {
            if (mic.active)
                mic.stop();
            if (recognition.listening)
                recognition.stop();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [speaking]);
    const micOrRecognitionActive = recognition.supported ? recognition.listening : mic.active;
    const orbState = speaking ? "speaking" : sending || preparingToSpeak ? "thinking" : micOrRecognitionActive ? "listening" : "idle";
    const orbLevel = speaking
        ? pollyAudio.speaking
            ? -1 // no live level -- usePollyAudio.ts dropped Web Audio entirely (two different node
            // types both still popped on a real phone; see that file's own doc comment). The orb
            // falls back to its own synthetic "speaking" energy curve instead.
            : ttsFallback.pulse
        : mic.active
            ? mic.level
            : recognition.listening
                ? -1 // no accessible live stream when using SpeechRecognition (see toggleConversation) -- -1 tells
                // the orb (orb/use-orb-animator.ts) to fall back to its own synthetic "listening" pulse
                // instead of a real level, same mechanism it already uses for every other state.
                : 0;
    const micError = recognition.error ?? mic.error;
    // `ParticlesOrb` reads `levelRef.current` from its own rAF loop (upstream's design, see
    // orb/use-orb-animator.ts) rather than taking `level` as a re-rendered prop -- cheaper for a
    // value that changes every animation frame.
    useEffect(() => {
        orbLevelRef.current = orbLevel;
    }, [orbLevel]);
    const [orbColorFrom, orbColorTo] = orbState === "speaking"
        ? [directory.accent ?? "#c41e1e", lightenHex(directory.accent ?? "#c41e1e")]
        : orbState === "thinking"
            ? STATE_GRADIENT.thinking
            : orbState === "listening"
                ? STATE_GRADIENT.listening
                : STATE_GRADIENT.idle;
    // Continuous "conversation mode" (user's own request): one tap turns the whole hands-free loop on
    // and starts listening; `speakReply`/the "ended without a result" effect above keep it listening
    // again after every answer with no further taps needed. A second tap turns the whole thing off --
    // the button flips to a stop icon while active specifically so it's clear that's what a second tap
    // does, rather than just "mute the mic for a moment."
    const toggleConversation = () => {
        if (conversationActive) {
            setConversationActive(false);
            if (recognition.supported)
                recognition.stop();
            else
                mic.stop();
            pollyAudio.stop(); // interrupt whatever's currently speaking (a filler clip or a reply) too
            return;
        }
        // Real user gesture, right here -- unlocks audio playback synchronously so the reply this mic
        // tap eventually triggers (recognition -> auto-send -> Polly reply) can actually play, even
        // though by the time it's ready there's no gesture left for the browser to check (see
        // usePollyAudio.ts's own doc comment on `unlock`).
        pollyAudio.unlock();
        setConversationActive(true);
        // `mic` (getUserMedia, for a real live orb level) and `recognition` (the browser's own
        // SpeechRecognition, which captures the mic independently) each trigger their OWN separate
        // microphone permission prompt -- confirmed live as a real bug, two prompts for what's
        // conceptually one "let me use your mic" action. `startListening` only ever starts one:
        // recognition is what actually matters for a real voice turn, so `mic`'s getUserMedia-based
        // level is only used as a fallback in browsers where SpeechRecognition isn't supported at all
        // (Firefox) -- continuous mode has nothing to loop on there (no transcript ever arrives), so it
        // behaves as a plain one-shot visual toggle in that fallback case.
        startListening();
    };
    const revealTagline = () => {
        setTaglineVisible(true);
        if (taglineTimerRef.current)
            clearTimeout(taglineTimerRef.current);
        taglineTimerRef.current = setTimeout(() => setTaglineVisible(false), 4500);
    };
    const ownerName = directory.ownerName ?? "This Assistant";
    const rootStyle = { "--accent": directory.accent ?? "#2f6f7d" };
    const stateLabel = speaking ? `${ownerName} is talking…` : sending || preparingToSpeak ? "Thinking…" : micOrRecognitionActive ? "Listening…" : "";
    return (_jsxs("div", { className: "vo-root", style: rootStyle, children: [_jsxs("div", { className: "vo-header", children: [_jsxs("div", { className: "vo-header-icons", children: [_jsx("button", { className: "vo-icon-btn", "aria-pressed": mode === "chat", "aria-label": mode === "voice" ? "Switch to text chat" : "Switch to voice", onClick: () => setMode((m) => (m === "voice" ? "chat" : "voice")), children: mode === "voice" ? _jsx(ChatIcon, {}) : _jsx(MicIcon, {}) }), _jsx("button", { className: "vo-icon-btn vo-help-btn", "aria-pressed": helpOpen, "aria-label": "Help", onClick: () => setHelpOpen((v) => !v), children: "?" })] }), _jsx("button", { className: "vo-title-btn", onClick: revealTagline, "aria-label": "Show description", children: _jsx("h1", { children: ownerName }) }), _jsx("p", { className: `vo-tagline${taglineVisible ? " vo-tagline-visible" : ""}`, children: directory.tagline })] }), _jsxs("div", { className: "vo-stage", children: [_jsx("div", { className: "vo-canvas-wrap", ref: orbWrapRef, children: _jsx(ParticlesOrb, { state: orbState, size: orbSize, levelRef: orbLevelRef, colorFrom: orbColorFrom, colorTo: orbColorTo, label: `${ownerName} orb` }) }), stateLabel && _jsx("div", { className: "vo-state-label", children: stateLabel }), mode === "voice" && (_jsx("button", { className: "vo-mic-icon-btn", "aria-pressed": conversationActive, "aria-label": conversationActive ? "Stop talking with " + ownerName : "Start talking with " + ownerName, onClick: toggleConversation, children: conversationActive ? _jsx(StopIcon, {}) : _jsx(MicIcon, {}) })), (micError || pollyAudio.error) && _jsx("div", { className: "vo-mic-hint", children: micError ?? pollyAudio.error }), helpOpen && !micError && !pollyAudio.error && (_jsx("div", { className: "vo-mic-hint", children: mode === "voice" && !recognition.supported
                            ? `Your browser doesn't support in-page speech recognition yet — the mic still makes the orb react to your voice, but switch to chat to actually talk to ${ownerName}.`
                            : mode === "voice"
                                ? conversationActive
                                    ? `Listening — just talk whenever you're ready, ${ownerName} will keep responding. Tap stop when you're done.`
                                    : `Tap the mic to start talking — ${ownerName} will keep listening and responding until you tap stop.`
                                : `Type your question below — ${ownerName} will answer back.` })), helpOpen && (_jsxs("label", { className: "vo-speak-toggle", children: [_jsx("input", { type: "checkbox", checked: speakAloud, onChange: (e) => setSpeakAloud(e.target.checked) }), "Speak replies aloud"] }))] }), helpOpen && (_jsxs("div", { className: "vo-transcript", children: [messages.length === 0 && _jsxs("div", { className: "vo-mic-hint", children: ["No messages yet \u2014 ask ", ownerName, " anything above or below."] }), messages.map((m, i) => (_jsx("div", { className: `vo-msg ${m.from}`, children: m.text }, i))), sending && _jsx("div", { className: "vo-msg assistant", children: "\u2026" }), _jsx("div", { ref: messagesEndRef })] })), toast && _jsx("div", { className: "vo-toast", children: toast }), mode === "chat" && (_jsxs("form", { className: "vo-composer", onSubmit: (e) => {
                    e.preventDefault();
                    pollyAudio.unlock();
                    sendMessage(input);
                }, children: [_jsx("input", { type: "text", value: input, onChange: (e) => setInput(e.target.value), placeholder: "Type a message", "aria-label": "Message", disabled: sending }), _jsx("button", { type: "submit", disabled: !input.trim() || sending, children: "Send" })] }))] }));
}
